// src/pages/banana/Banana.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Link as MuiLink,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField // Import TextField untuk pencarian
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search'; // Import icon pencarian
import * as XLSX from "xlsx";

import { useTheme } from "@mui/material/styles";
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../firebase";
import { ref, onValue, push, update } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper function to convert Excel date number to YYYY-MM-DD string
const excelDateToJSDate = (excelDate) => {
    if (typeof excelDate === 'number' && excelDate > 0) {
        const date = new Date(Math.round((excelDate - 25569) * 86400 * 1000));
        return date.toISOString().split('T')[0];
    }
    return excelDate;
};

// Styles for TableHead Cells
const headerCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 2
};

const subHeaderCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 1,
    top: 56
};

function Banana() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState(''); // State untuk pencarian

    // State untuk data konfigurasi dropdown
    const [kodeWilayahList, setKodeWilayahList] = useState([]);
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [helperData, setHelperData] = useState({});

    // Filter states
    const [filterStatusLokasi, setFilterStatusLokasi] = useState('');
    const [filterTahun, setFilterTahun] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_banana/tb_penyakit
                const penyakitRef = ref(database, "db_banana/tb_penyakit");
                const penyakitSnapshot = await onValuePromise(penyakitRef);
                const penyakitData = penyakitSnapshot.val();
                const loadedPenyakit = [];
                if (penyakitData) {
                    Object.keys(penyakitData).forEach(key => {
                        loadedPenyakit.push({ 
                            firebaseKey: key, 
                            ...penyakitData[key],
                            // Pastikan field status ada
                            status: penyakitData[key].status || '' 
                        });
                    });
                }
                setOriginalDataList(loadedPenyakit);
                setDataList(loadedPenyakit);

                // Fetch lookup data for Kode Wilayah
                const kodeWilayahConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kode_wilayah");
                const kodeWilayahConfigSnapshot = await onValuePromise(kodeWilayahConfigRef);
                const kodeWilayahConfigData = kodeWilayahConfigSnapshot.val();
                if (kodeWilayahConfigData) {
                    const list = Object.keys(kodeWilayahConfigData).map(key => ({
                        id: key,
                        nama_kode_wilayah: kodeWilayahConfigData[key].nama_kode_wilayah,
                    }));
                    setKodeWilayahList(list);
                }

                // Fetch lookup data for Status Lokasi
                const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
                const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
                if (statusLokasiConfigData) {
                    const list = Object.keys(statusLokasiConfigData).map(key => ({
                        id: key,
                        nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                        indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                    }));
                    setStatusLokasiList(list);
                }

                // Fetch data from db_config/db_helper
                const helperRef = ref(database, "db_config/db_helper/tb_wilayah_plant_care");
                const helperSnapshot = await onValuePromise(helperRef);
                const helperRawData = helperSnapshot.val();
                if (helperRawData) {
                    setHelperData(helperRawData);
                } else {
                    setHelperData({});
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.lokasi && item.lokasi.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) {
        setSnackbar({
            open: true,
            message: "Tidak ada file yang dipilih.",
            severity: "warning",
        });
        return;
    }

    setImportLoading(true);
    setSnackbar({
        open: true,
        message: "Memulai proses import...",
        severity: "info",
    });

    const reader = new FileReader();
    reader.onload = async (e) => {
        try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // Gunakan option { raw: false } untuk mendapatkan nilai yang diformat sebagai string
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });

            const penyakitRef = ref(database, "db_banana/tb_penyakit");
            const existingPenyakitSnapshot = await onValuePromise(penyakitRef);
            const existingPenyakitData = existingPenyakitSnapshot.val() || {};

            // Create a map for quick lookup: id_penyakit -> firebaseKey for existing data
            const existingPenyakitMap = new Map();
            Object.keys(existingPenyakitData).forEach(fbKey => {
                const record = existingPenyakitData[fbKey];
                existingPenyakitMap.set(record.id_penyakit, fbKey);
            });

            const importTimestamp = new Date().toISOString().split('T')[0];

            let importedCount = 0;
            let updatedCount = 0;
            let ignoredCount = 0;

            for (const item of jsonData) {
                const tahun = item["Tahun"];
                const lokasi = item["Lokasi"];
                const status = item["Status"];
                const hill = item["Hill"]; // Ambil data Hill dari Excel
                
                // Basic validation for required fields
                if (!tahun || !lokasi || !status) {
                    ignoredCount++;
                    console.warn("Ignoring row due to missing Tahun, Lokasi or Status:", item);
                    continue;
                }

                // Construct idPenyakit dari kombinasi tahun + lokasi + status
                const idPenyakit = `${tahun}${lokasi}${status}`;

                // Fungsi untuk mempertahankan format dengan titik dan 2 digit desimal
                const preserveDecimalFormat = (value) => {
                    if (value === undefined || value === null || value === '') return '0.00';
                    
                    // Jika sudah string, konversi koma ke titik
                    if (typeof value === 'string') {
                        // Bersihkan dan validasi format
                        let cleanedValue = value.toString().trim().replace(',', '.');
                        
                        // Parse ke number untuk pembulatan
                        const num = parseFloat(cleanedValue);
                        
                        // Jika valid number, format ke 2 digit desimal
                        if (!isNaN(num)) {
                            return num.toFixed(2);
                        }
                        
                        return '0.00';
                    }
                    
                    // Jika number, format ke 2 digit desimal
                    if (typeof value === 'number') {
                        return value.toFixed(2);
                    }
                    
                    return '0.00';
                };

                // Fungsi untuk memformat data Hill (menghilangkan koma/format ribuan)
                const formatHillData = (value) => {
                    if (value === undefined || value === null || value === '') return '0';
                    
                    // Jika string, hapus koma dan karakter non-digit
                    if (typeof value === 'string') {
                        return value.toString().replace(/,/g, '').replace(/[^\d.]/g, '');
                    }
                    
                    // Jika number, konversi ke string tanpa koma
                    if (typeof value === 'number') {
                        return value.toString().replace(/,/g, '');
                    }
                    
                    return '0';
                };

                const dataToSave = {
                    id_penyakit: idPenyakit,
                    tahun: tahun,
                    lokasi: lokasi,
                    status: status,
                    hill: formatHillData(hill), // Simpan data Hill yang sudah diformat
                    jenis_penyakit: {
                        panama_disease: preserveDecimalFormat(item["Panama Disease"]),
                        moko: preserveDecimalFormat(item["Moko"]),
                        tip_over: preserveDecimalFormat(item["Tip Over"]),
                        blowdown: preserveDecimalFormat(item["Blowdown"])
                    },
                    last_imported_at: importTimestamp
                };

                const firebaseKey = existingPenyakitMap.get(idPenyakit);

                if (firebaseKey) {
                    // Update existing record
                    await update(ref(database, `db_banana/tb_penyakit/${firebaseKey}`), dataToSave);
                    updatedCount++;
                } else {
                    // Add new record
                    await push(penyakitRef, dataToSave);
                    importedCount++;
                }
            }

            setSnackbar({
                open: true,
                message: `Impor selesai! Berhasil menambahkan ${importedCount} data baru dan memperbarui ${updatedCount} data. ${ignoredCount} data diabaikan (data tidak lengkap).`,
                severity: "success",
            });
            
            // Re-fetch data to update the table display
            const updatedPenyakitSnapshot = await onValuePromise(penyakitRef);
            const updatedPenyakitData = updatedPenyakitSnapshot.val() || {};
            const updatedLoadedPenyakit = [];
            Object.keys(updatedPenyakitData).forEach(key => {
                updatedLoadedPenyakit.push({ firebaseKey: key, ...updatedPenyakitData[key] });
            });
            setDataList(updatedLoadedPenyakit);
            setOriginalDataList(updatedLoadedPenyakit);

        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            handleMenuClose();
            setImportLoading(false);
        }
    };

    reader.readAsArrayBuffer(file);
};

// Di bagian render tabel, tambahkan fungsi untuk memformat Hill dalam format ribuan
const formatHill = (value) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    
    // Konversi ke number
    const num = typeof value === 'string' 
        ? parseFloat(value.replace(/,/g, '')) 
        : value;
    
    // Format ke string dengan pemisah ribuan tanpa desimal
    if (!isNaN(num)) {
        return Math.round(num).toLocaleString('id-ID');
    }
    
    return value;
};

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

const handleFilterData = () => {
  let filtered = [...originalDataList];

  if (filterStatusLokasi) {
    filtered = filtered.filter(item => item.status === filterStatusLokasi);
  }
  if (filterTahun) {
    filtered = filtered.filter(item => item.tahun === filterTahun);
  }

  setDataList(filtered);
  setPage(0);
};

    const handleResetFilter = () => {
        setFilterStatusLokasi('');
        setFilterTahun('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Get unique years for filter dropdown
    const tahunList = [...new Set(originalDataList.map(item => item.tahun))].filter(Boolean).sort();

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status Lokasi</InputLabel>
                            <Select
                                value={filterStatusLokasi}
                                label="Status Lokasi"
                                onChange={(e) => setFilterStatusLokasi(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {statusLokasiList.map((status) => (
                                    <MenuItem key={status.id} value={status.nama_status_lokasi}>
                                        {status.nama_status_lokasi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tahun</InputLabel>
                            <Select
                                value={filterTahun}
                                label="Tahun"
                                onChange={(e) => setFilterTahun(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {tahunList.map((tahun) => (
                                    <MenuItem key={tahun} value={tahun}>
                                        {tahun}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Data Penyakit Pisang
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Tambahkan TextField untuk pencarian sebelum icon filter */}
                        <TextField
                            size="small"
                            placeholder="Cari berdasarkan lokasi..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
                    <Table stickyHeader aria-label="banana disease table">
<TableHead>
  {/* Baris Pertama */}
  <TableRow>
    <TableCell rowSpan={2} align="center" sx={headerCellStyle}>Tahun</TableCell>
    <TableCell rowSpan={2} align="center" sx={headerCellStyle}>Lokasi</TableCell>
    <TableCell rowSpan={2} align="center" sx={headerCellStyle}>Status</TableCell>
    <TableCell rowSpan={2} align="center" sx={headerCellStyle}>Ha</TableCell>
    <TableCell colSpan={4} align="center" sx={headerCellStyle}>Jenis Penyakit</TableCell>
  </TableRow>
  
  {/* Baris Kedua */}
  <TableRow>
    <TableCell align="center" sx={subHeaderCellStyle}>Panama Disease</TableCell>
    <TableCell align="center" sx={subHeaderCellStyle}>Moko</TableCell>
    <TableCell align="center" sx={subHeaderCellStyle}>Tip Over</TableCell>
    <TableCell align="center" sx={subHeaderCellStyle}>Blowdown</TableCell>
  </TableRow>
</TableHead>

<TableBody>
  {displayedDataList.length > 0 ? (
    displayedDataList
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row) => {
        // Lookup nama_status_lokasi dan indicator
        const statusLokasiItem = statusLokasiList.find(
          (item) => item.nama_status_lokasi === row.status
        );
        const displayStatusLokasi = statusLokasiItem?.nama_status_lokasi || row.status || 'N/A';
        const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

        // Format angka dengan titik sebagai desimal dan 2 digit
        const formatDecimal = (value) => {
            if (value === undefined || value === null || value === '') return 'N/A';
            
            // Jika sudah string, pastikan format benar
            if (typeof value === 'string') {
                // Konversi koma ke titik jika ada
                let formattedValue = value.replace(',', '.');
                
                // Parse ke number untuk pembulatan
                const num = parseFloat(formattedValue);
                
                // Jika valid number, format ke 2 digit desimal
                if (!isNaN(num)) {
                    return num.toFixed(2);
                }
                
                return value; // Return as-is jika tidak bisa diparse
            }
            
            // Jika number, format ke 2 digit desimal
            if (typeof value === 'number') {
                return value.toFixed(2);
            }
            
            return 'N/A';
        };

        return (
          <TableRow key={row.firebaseKey}>
            <TableCell>{row.tahun || 'N/A'}</TableCell>
            <TableCell>{row.lokasi || 'N/A'}</TableCell>
            <TableCell>
              {displayStatusLokasi ? (
                <Chip
                  label={displayStatusLokasi}
                  style={{ backgroundColor: statusIndicatorColor, color: theme.palette.getContrastText(statusIndicatorColor) }}
                  size="small"
                />
              ) : 'N/A'}
            </TableCell>
            <TableCell>{row.hill ? row.hill.toString().replace(',', '.') : 'N/A'}</TableCell>
            <TableCell>{formatDecimal(row.jenis_penyakit?.panama_disease)}</TableCell>
            <TableCell>{formatDecimal(row.jenis_penyakit?.moko)}</TableCell>
            <TableCell>{formatDecimal(row.jenis_penyakit?.tip_over)}</TableCell>
            <TableCell>{formatDecimal(row.jenis_penyakit?.blowdown)}</TableCell>
          </TableRow>
        );
      })
  ) : (
    <TableRow>
      <TableCell colSpan={7} align="center">
        {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data penyakit pisang.'}
      </TableCell>
    </TableRow>
  )}
</TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={displayedDataList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Banana;